import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
	CustomIconButton,
	CustomTooltip,
	Hierarchy,
	icon,
	Preview,
	VerticalDivider,
} from "../../../../../../common/components";
import styles from "./DetectedContentRow.module.css";
import { dangerousSetInnerHTML } from "../../../../../../common/utils";
import { hasCompanyPermission, hasPermission, Permissions, translate } from "../../../../../../common/providers";
import { useApi, useTrackEvent } from "../../../../../../common/hooks";
import { AiDetectionsService } from "../../../../../../api";

export default function DetectedContentRow({ source, onLikeSource }) {
	const [openPreview, setOpenPreview] = useState(false);
	const trackEvent = useTrackEvent();
	const projectId = useSelector(({ context }) => context?.project?.id);
	const { call: likeValueSource } = useApi(AiDetectionsService.likeValueSource);
	const {
		location,
		informationId,
		documentId,
		page,
		type: informationType,
		rectangleDTO,
		documentName,
		content,
	} = source.details || {};
	const { isManuallyAdded, userDisplayName } = source;
	const hasRights = useMemo(
		() =>
			hasPermission([Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER, Permissions.PROJECT_MEMBER]) &&
			hasCompanyPermission([Permissions.PROJECT_LEADER]),
		[]
	);
	const wordingLikeButtons = (likeStatus) => {
		if (hasRights) {
			if (likeStatus) {
				if (source?.isVerified) {
					return translate("dashboards.project-sheet.section-details.like.allowed.validated");
				}
				return translate("dashboards.project-sheet.section-details.like.allowed.neutral");
			}
			if (source?.isVerified || source?.isVerified === null) {
				return translate("dashboards.project-sheet.section-details.dislike.allowed.neutral");
			}
			return translate("dashboards.project-sheet.section-details.dislike.allowed.non-validated");
		}
		if (source?.isVerified) {
			return translate("dashboards.project-sheet.section-details.any.non-allowed.validated");
		}
		if (source?.isVerified === false) {
			return translate("dashboards.project-sheet.section-details.any.non-allowed.non-validated");
		}
		return translate("dashboards.project-sheet.section-details.any.non-allowed.neutral");
	};
	const handleClickDisplay = () => {
		setOpenPreview((prev) => !prev);
	};
	const handleLike = (newStatus) => {
		const { informationId: sourceId, fieldUuid } = source || {};
		if (hasRights && projectId && informationId && fieldUuid) {
			const statusToChange = newStatus !== source?.isVerified ? newStatus : null;
			likeValueSource({ projectId, fieldUuid, informationId: sourceId }, statusToChange)
				.then(() => {
					onLikeSource(source, statusToChange);
					trackEvent("Detection_Thumbs");
				})
				.catch(console.error);
		}
	};
	const sourceTypeWording = useMemo(
		() =>
			(isManuallyAdded &&
				translate("dashboards.project-sheet.section-details.manual-update-author", {
					author: userDisplayName,
				})) ||
			translate("dashboards.project-sheet.section-details.automatic-update"),
		[isManuallyAdded, userDisplayName]
	);
	return (
		<div className={styles.row}>
			<div className={styles.row__information}>
				<div className={styles["row__information--leftSide"]}>
					<Hierarchy
						enableFirstLocationCut
						isDangerousSetInnerHTML
						breadcrumbClassName={styles.breadcrumb}
						className={styles.breadcrumbContainer}
						documentName={documentName}
						hierarchy={location}
						popperClassName={styles.popper}
						separator={<span className={styles.breadcrumbContainer__separator}>/</span>}
					/>
				</div>
				<div className={styles["row__information--actions"]}>
					<CustomIconButton
						badgeClassName={styles.updateInfo__badge}
						btnClassName={styles.updateInfo__button}
						className={styles.updateInfo}
						icon={icon[(isManuallyAdded && "faUser") || "faWandMagicSparkles"]}
						iconClassName={styles.updateInfo__icon}
						tooltip={sourceTypeWording}
						variant="text"
					/>
					<CustomIconButton
						icon={icon.faFile}
						size="xs"
						tooltip={translate("common:btn.display")}
						variant="outlined"
						onClick={handleClickDisplay}
					/>
					<VerticalDivider className={styles.divider} />
					<CustomTooltip title={!hasRights && translate("common:permissions.insufficient-rights")}>
						<span>
							<CustomIconButton
								btnClassName={styles.like__button}
								data-has-rights={hasRights}
								data-is-on={!!source?.isVerified}
								disabled={!hasRights}
								icon={icon.faThumbsUp}
								iconColor={source?.isVerified && "var(--color-white)"}
								size="xs"
								tooltip={wordingLikeButtons(true)}
								variant="outlined"
								onClick={() => handleLike(true)}
							/>
						</span>
					</CustomTooltip>
					<CustomTooltip title={!hasRights && translate("common:permissions.insufficient-rights")}>
						<span>
							<CustomIconButton
								btnClassName={styles.dislike__button}
								data-has-rights={hasRights}
								data-is-on={source?.isVerified === false}
								disabled={!hasRights}
								icon={icon.faThumbsDown}
								iconColor={source?.isVerified === false && "var(--color-white)"}
								size="xs"
								tooltip={wordingLikeButtons(false)}
								variant="outlined"
								onClick={() => handleLike(false)}
							/>
						</span>
					</CustomTooltip>
				</div>
			</div>
			<p className={styles.row__description}>
				{content ? (
					dangerousSetInnerHTML({ text: content })
				) : (
					<>{translate("common:searchcard.image-matched")}</>
				)}
			</p>
			<Preview
				hasFolderIcon
				coordinates={rectangleDTO}
				docId={documentId}
				docName={documentName}
				infoId={informationId}
				infoType={informationType}
				location={location}
				open={openPreview}
				page={page}
				projectId={projectId}
				onClose={handleClickDisplay}
			/>
		</div>
	);
}
